import React, { Suspense } from 'react';

// --- Lazy Loaded Tool Modals ---
const MMECalculatorModal = React.lazy(() => import('./tools/MMECalculatorModal'));
const DoseAdjustModal = React.lazy(() => import('./tools/DoseAdjustModal'));
const IVCompatibilityModal = React.lazy(() => import('./tools/IVCompatibilityModal'));
const DrugInteractionModal = React.lazy(() => import('./tools/DrugInteractionModal'));
const PillIdentifierModal = React.lazy(() => import('./tools/PillIdentifierModal'));
const CounselingPointsModal = React.lazy(() => import('./tools/CounselingPointsModal'));
const AnticoagBridgingModal = React.lazy(() => import('./tools/AnticoagBridgingModal'));
const TherapeuticInterchangeModal = React.lazy(() => import('./tools/TherapeuticInterchangeModal'));
const DaysSupplyModal = React.lazy(() => import('./tools/DaysSupplyModal'));
const CompoundingCalcModal = React.lazy(() => import('./tools/CompoundingCalcModal'));
const StateLawModal = React.lazy(() => import('./tools/StateLawModal'));
const FederalLawModal = React.lazy(() => import('./tools/FederalLawModal'));
const AntibioticsModal = React.lazy(() => import('./tools/AntibioticsModal'));
const GuidelinesModal = React.lazy(() => import('./tools/GuidelinesModal'));
const DrugTaperingModal = React.lazy(() => import('./tools/DrugTaperingModal'));
const PediatricGuideModal = React.lazy(() => import('./tools/PediatricGuideModal'));
const PharmacokineticsModal = React.lazy(() => import('./tools/PharmacokineticsModal'));
const AnticholinergicBurdenModal = React.lazy(() => import('./tools/AnticholinergicBurdenModal'));
const InsulinToolModal = React.lazy(() => import('./tools/InsulinToolModal'));
const MissedDoseModal = React.lazy(() => import('./tools/MissedDoseModal'));
const VaccineToolModal = React.lazy(() => import('./tools/VaccineToolModal'));
const ClinicalCriteriaModal = React.lazy(() => import('./tools/ClinicalCriteriaModal'));
const AntidotesModal = React.lazy(() => import('./tools/AntidotesModal'));
const DrugShortageModal = React.lazy(() => import('./tools/DrugShortageModal'));
const LabValuesModal = React.lazy(() => import('./tools/LabValuesModal'));
const ConversionsModal = React.lazy(() => import('./tools/ConversionsModal'));
const OTCFinderModal = React.lazy(() => import('./tools/OTCFinderModal'));
const CSReferenceModal = React.lazy(() => import('./tools/CSReferenceModal'));
const DrugPriceFinderModal = React.lazy(() => import('./tools/DrugPriceFinderModal'));
const PAAssistantModal = React.lazy(() => import('./tools/PAAssistantModal'));
const SigCodesModal = React.lazy(() => import('./tools/SigCodesModal'));
const EmergencyGuideModal = React.lazy(() => import('./tools/EmergencyGuideModal'));
const AnalyzeRxModal = React.lazy(() => import('./tools/AnalyzeRxModal'));
const ClinicalTrialsModal = React.lazy(() => import('./tools/ClinicalTrialsModal'));
const DietaryGuideModal = React.lazy(() => import('./tools/DietaryGuideModal'));
const FormularyAnalyzerModal = React.lazy(() => import('./tools/FormularyAnalyzerModal'));
const HIVInfoModal = React.lazy(() => import('./tools/HIVInfoModal'));
const INRInfoModal = React.lazy(() => import('./tools/INRInfoModal'));
const PGxGuideModal = React.lazy(() => import('./tools/PGxGuideModal'));
const BiostatsModal = React.lazy(() => import('./tools/BiostatsModal'));
const CalculatorsModal = React.lazy(() => import('./tools/CalculatorsModal'));
const CannabinoidsModal = React.lazy(() => import('./tools/CannabinoidsModal'));
const CETrackerModal = React.lazy(() => import('./tools/CETrackerModal'));
const ChemoManModal = React.lazy(() => import('./tools/ChemoManModal'));
const CompoundingFormulasModal = React.lazy(() => import('./tools/CompoundingFormulasModal'));
const MnemonicsModal = React.lazy(() => import('./tools/MnemonicsModal'));
const VitaminsModal = React.lazy(() => import('./tools/VitaminsModal'));
const FormsFinderModal = React.lazy(() => import('./tools/FormsFinderModal'));
const FitnessGuideModal = React.lazy(() => import('./tools/FitnessGuideModal'));
const PregnancyLactationModal = React.lazy(() => import('./tools/PregnancyLactationModal'));
const GeriatricGuideModal = React.lazy(() => import('./tools/GeriatricGuideModal'));
const MTMCMRModal = React.lazy(() => import('./tools/MTMCMRModal'));

interface ToolRendererProps {
    activeTool: string | null;
    onClose: () => void;
}

const ToolLoading: React.FC = () => (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center p-4 z-50">
        <div className="bg-[#1a2a47] rounded-2xl border border-blue-900/50 w-full max-w-md p-8 flex flex-col items-center">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-yellow-400 mb-4"></div>
            <p className="text-gray-300 font-semibold">Loading tool...</p>
        </div>
    </div>
);

const ToolRenderer: React.FC<ToolRendererProps> = ({ activeTool, onClose }) => {
    if (!activeTool) return null;

    return (
        <Suspense fallback={<ToolLoading />}>
            {(() => {
                switch (activeTool) {
                    case 'MME Calculator': return <MMECalculatorModal onClose={onClose} />;
                    case 'Dose Adjust': return <DoseAdjustModal onClose={onClose} />;
                    case 'IV Compatibility': return <IVCompatibilityModal onClose={onClose} />;
                    case 'Interactions': return <DrugInteractionModal onClose={onClose} />;
                    case 'Pill Identifier': return <PillIdentifierModal onClose={onClose} />;
                    case 'Patient Counseling': return <CounselingPointsModal onClose={onClose} />;
                    case 'Anticoag Bridging': return <AnticoagBridgingModal onClose={onClose} />;
                    case 'Therapeutic Interchange': return <TherapeuticInterchangeModal onClose={onClose} />;
                    case 'Days Supply': return <DaysSupplyModal onClose={onClose} />;
                    case 'Compounding Calculator': return <CompoundingCalcModal onClose={onClose} />;
                    case 'State Law Reference': return <StateLawModal onClose={onClose} />;
                    case 'Federal Laws': return <FederalLawModal onClose={onClose} />;
                    case 'Antibiotics': return <AntibioticsModal onClose={onClose} />;
                    case 'Guidelines': return <GuidelinesModal onClose={onClose} />;
                    case 'Drug Tapering': return <DrugTaperingModal onClose={onClose} />;
                    case 'Pediatric Guide': return <PediatricGuideModal onClose={onClose} />;
                    case 'Pharmacokinetics': return <PharmacokineticsModal onClose={onClose} />;
                    case 'Anticholinergic Burden': return <AnticholinergicBurdenModal onClose={onClose} />;
                    case 'Insulin Tool': return <InsulinToolModal onClose={onClose} />;
                    case 'Missed Dose': return <MissedDoseModal onClose={onClose} />;
                    case 'Vaccine Tool': return <VaccineToolModal onClose={onClose} />;
                    case 'Clinical Criteria': return <ClinicalCriteriaModal onClose={onClose} />;
                    case 'Antidotes': return <AntidotesModal onClose={onClose} />;
                    case 'Drug Shortage Tracker': return <DrugShortageModal onClose={onClose} />;
                    case 'Lab Charts': return <LabValuesModal onClose={onClose} />;
                    case 'Conversions': return <ConversionsModal onClose={onClose} />;
                    case 'OTC Finder': return <OTCFinderModal onClose={onClose} />;
                    case 'CS Reference': return <CSReferenceModal onClose={onClose} />;
                    case 'Drug Price Finder': return <DrugPriceFinderModal onClose={onClose} />;
                    case 'PA Assistant': return <PAAssistantModal onClose={onClose} />;
                    case 'Sig Codes': return <SigCodesModal onClose={onClose} />;
                    case 'Emergency Guide': return <EmergencyGuideModal onClose={onClose} />;
                    case 'Analyze Rx': return <AnalyzeRxModal onClose={onClose} />;
                    case 'Clinical Trials': return <ClinicalTrialsModal onClose={onClose} />;
                    case 'Dietary Guide': return <DietaryGuideModal onClose={onClose} />;
                    case 'Formulary Analyzer': return <FormularyAnalyzerModal onClose={onClose} />;
                    case 'HIV Info': return <HIVInfoModal onClose={onClose} />;
                    case 'INR Info': return <INRInfoModal onClose={onClose} />;
                    case 'PGx Guide': return <PGxGuideModal onClose={onClose} />;
                    case 'Biostats': return <BiostatsModal onClose={onClose} />;
                    case 'Calculators': return <CalculatorsModal onClose={onClose} />;
                    case 'Cannabinoids': return <CannabinoidsModal onClose={onClose} />;
                    case 'CE Tracker': return <CETrackerModal onClose={onClose} />;
                    case 'Chemo Man': return <ChemoManModal onClose={onClose} />;
                    case 'Compounding Formulas': return <CompoundingFormulasModal onClose={onClose} />;
                    case 'Mnemonics': return <MnemonicsModal onClose={onClose} />;
                    case 'Vitamins': return <VitaminsModal onClose={onClose} />;
                    case 'Forms Finder': return <FormsFinderModal onClose={onClose} />;
                    case 'Fitness Guide': return <FitnessGuideModal onClose={onClose} />;
                    case 'Pregnancy & Lactation': return <PregnancyLactationModal onClose={onClose} />;
                    case 'Geriatric Guide': return <GeriatricGuideModal onClose={onClose} />;
                    case 'MTM/CMR': return <MTMCMRModal onClose={onClose} />;
                    default: return null;
                }
            })()}
        </Suspense>
    );
};

export default ToolRenderer;